import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { FiLoader } from "react-icons/fi";
import toast, { Toaster } from "react-hot-toast";
import { FiDownload, FiHome } from "react-icons/fi";
import {
  Search,
  Building2,
  Users,
  BedDouble,
  Home,
  Filter,
  CheckCircle,
  XCircle,
  DoorOpen,
  LayoutGrid,
} from "lucide-react";

function RoomsPage() {
  const { residenceId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const studentId = location.state?.studentId;
  const studentFirstName = location.state?.studentFirstName || "Student";
  const studentLastName = location.state?.studentLastName || "";
  const residenceNameFromState = location.state?.residenceName;

  const [processingRoomId, setProcessingRoomId] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [residenceName, setResidenceName] = useState(
    residenceNameFromState || `Residence #${residenceId}`
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedBlock, setSelectedBlock] = useState("");
  const [selectedFloor, setSelectedFloor] = useState("");
  const [loading, setLoading] = useState(false);
  const [availabilityFilter, setAvailabilityFilter] = useState("All");
  const [highlightAvailable, setHighlightAvailable] = useState(true);

  const [showAssignConfirm, setShowAssignConfirm] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);

  // Fetch residence name
  const fetchResidenceName = useCallback(async () => {
    if (residenceNameFromState || !residenceId) return;
    try {
      const res = await fetch(`https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/Residence/${residenceId}/stats`);
      if (!res.ok) throw new Error("Failed to fetch residence stats");
      const data = await res.json();
      setResidenceName(data.Residence?.Name || `Residence #${residenceId}`);
    } catch (err) {
      console.error(err);
      setResidenceName(`Residence #${residenceId}`);
    }
  }, [residenceId, residenceNameFromState]);

  // Fetch rooms
  const fetchRooms = useCallback(async () => {
    if (!residenceId) return;
    setLoading(true);
    try {
      const res = await fetch(`https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/Room/residence/${residenceId}`);
      if (!res.ok) throw new Error("Failed to fetch rooms.");
      const data = await res.json();
      const formatted = data.map((room) => ({
        roomId: room.roomId,
        roomNumber: room.roomNumber,
        capacity: room.capacity,
        occupied: room.occupied,
        remaining: room.remaining,
        building: room.building,
        floor: room.floor,
        status: room.remaining === 0 ? "Full" : "Available",
      }));
      setRooms(formatted);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load rooms. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [residenceId]);

  useEffect(() => {
    fetchResidenceName();
    fetchRooms();
  }, [fetchResidenceName, fetchRooms]);

  const availableBlocks = [...new Set(rooms.map((r) => r.building))];
  const availableFloors = selectedBlock
    ? [...new Set(rooms.filter((r) => r.building === selectedBlock).map((r) => r.floor))]
    : [];

  const filteredRooms = rooms.filter((room) => {
    return (
      room.roomNumber.toLowerCase().includes(searchQuery.toLowerCase()) &&
      (selectedBlock ? room.building === selectedBlock : true) &&
      (selectedFloor ? room.floor === Number(selectedFloor) : true) &&
      (availabilityFilter === "All"
        ? true
        : availabilityFilter === "Available"
          ? room.status === "Available"
          : room.status === "Full")
    );
  });

  const groupedRooms = filteredRooms.reduce((acc, room) => {
    if (!acc[room.building]) acc[room.building] = {};
    if (!acc[room.building][room.floor]) acc[room.building][room.floor] = [];
    acc[room.building][room.floor].push(room);
    return acc;
  }, {});

  // Summary stats
  const totalRooms = rooms.length;
  const totalCapacity = rooms.reduce((sum, r) => sum + (r.capacity || 0), 0);
  const totalOccupied = rooms.reduce((sum, r) => sum + (r.occupied || 0), 0);
  const availableCount = rooms.filter((r) => r.status === "Available").length;
  const fullCount = rooms.filter((r) => r.status === "Full").length;
  const occupancyRate = totalCapacity > 0 ? Math.round((totalOccupied / totalCapacity) * 100) : 0;

  // Download all students assigned to the residence
  const downloadResidenceStudentsCSV = async () => {
    try {
      const res = await fetch(`https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/residence/${residenceId}/students/download`);
      if (!res.ok) {
        toast.error("Failed to fetch students for this residence.");
        return;
      }

      const allStudents = await res.json();
      if (allStudents.length === 0) {
        toast("No students assigned in this residence.");
        return;
      }

      const headers = Object.keys(allStudents[0]);
      const csvRows = [headers.join(",")];

      allStudents.forEach(student => {
        csvRows.push(
          headers.map(h => `"${student[h] ?? ""}"`).join(",")
        );
      });

      const csvContent = csvRows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${residenceName}_Assigned_Students.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      toast.success("CSV downloaded successfully!");
    } catch (err) {
      console.error(err);
      toast.error("Failed to download CSV.");
    }
  };

  // Assign student to room
  const handleAssign = async (roomId) => {
    if (!studentId) return toast.error("No student selected for assignment.");
    setProcessingRoomId(roomId);
    try {
      const leaseCheckRes = await fetch(`https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/LeaseGenerator/${studentId}`);
      if (leaseCheckRes.ok) {
        toast.error("Lease has already been generated for this student.");
        setProcessingRoomId(null);
        return;
      }

      const assignRes = await fetch(
        `https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/Room/assign/${roomId}/${studentId}`,
        { method: "POST" }
      );
      const assignResult = await assignRes.json();
      if (!assignRes.ok) {
        toast.error(assignResult.message || "Failed to assign student.");
        setProcessingRoomId(null);
        return;
      }

      const leaseRes = await fetch(
        `https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/LeaseGenerator/generate/${roomId}/${studentId}`,
        { method: "POST" }
      );
      const leaseResult = await leaseRes.json();
      if (!leaseRes.ok) {
        toast.error(leaseResult.message || "Failed to assign and generate lease.");
        setProcessingRoomId(null);
        return;
      }

      toast.success("Student assigned and lease generated successfully!");
      await fetchRooms();
    } catch (err) {
      console.error(err);
      toast.error("An error occurred. Check console for details.");
    } finally {
      setProcessingRoomId(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-5 md:p-8">
      <Toaster position="top-right" />

      <div className="w-full space-y-6">
        {/* Header Banner */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 border-t-4 border-t-blue-950 p-6">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gray-100 rounded-xl">
                <Building2 className="w-6 h-6 text-gray-700" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{residenceName}</h1>
                <p className="text-sm text-gray-500 mt-0.5">
                  {studentId
                    ? `Assigning a room for ${studentFirstName} ${studentLastName}`
                    : "Room allocation & occupancy management"}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={downloadResidenceStudentsCSV}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 shadow-sm transition"
              >
                <FiDownload className="w-4 h-4" />
                Export Students
              </button>
              <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 transition"
              >
                <FiHome className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>
        </div>

        {/* Assigning banner */}
        {studentId && (
          <div className="bg-gradient-to-r from-violet-50 via-white to-blue-50 rounded-xl shadow-sm border border-violet-100 px-5 py-3 flex items-center gap-3">
            <p className="text-sm text-gray-700">
              Selecting a room for{" "}
              <span className="font-semibold text-gray-900">
                {studentFirstName} {studentLastName}
              </span>
              . Rooms with open spots are highlighted below — click any available room to assign.
            </p>
          </div>
        )}

        {/* Stat Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 shadow-lg shadow-blue-200 text-white">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <LayoutGrid className="w-5 h-5 text-white" />
              </div>
            </div>
            <p className="text-sm text-blue-100">Total Rooms</p>
            <p className="text-2xl font-bold mt-1">{totalRooms}</p>
          </div>

          <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-5 shadow-lg shadow-emerald-200 text-white">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <CheckCircle className="w-5 h-5 text-white" />
              </div>
            </div>
            <p className="text-sm text-emerald-100">Available</p>
            <p className="text-2xl font-bold mt-1">{availableCount}</p>
          </div>

          <div className="bg-gradient-to-br from-rose-500 to-rose-600 rounded-xl p-5 shadow-lg shadow-rose-200 text-white">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <XCircle className="w-5 h-5 text-white" />
              </div>
            </div>
            <p className="text-sm text-rose-100">Full</p>
            <p className="text-2xl font-bold mt-1">{fullCount}</p>
          </div>

          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-5 shadow-lg shadow-purple-200 text-white">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <Users className="w-5 h-5 text-white" />
              </div>
            </div>
            <p className="text-sm text-purple-100">Occupancy Rate</p>
            <p className="text-2xl font-bold mt-1">{occupancyRate}%</p>
          </div>
        </div>

        {/* Filters Card */}
        <div className="bg-white rounded-xl shadow-md border border-gray-200 border-t-4 border-t-blue-950 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-slate-50 to-gray-50">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <Filter className="w-4 h-4 text-indigo-600" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Filter Rooms</h3>
                <p className="text-xs text-gray-500">Narrow down by block, floor, or availability</p>
              </div>
            </div>
          </div>

          <div className="p-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Room number..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-300 text-gray-900 placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 bg-white transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">Block</label>
                <select
                  value={selectedBlock}
                  onChange={(e) => {
                    setSelectedBlock(e.target.value);
                    setSelectedFloor("");
                  }}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 text-gray-900 bg-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                >
                  <option value="">All Blocks</option>
                  {availableBlocks.map((b) => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
              </div>

              {availableFloors.length > 0 && (
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">Floor</label>
                  <select
                    value={selectedFloor}
                    onChange={(e) => setSelectedFloor(e.target.value)}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 text-gray-900 bg-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                  >
                    <option value="">All Floors</option>
                    {availableFloors.map((f) => (
                      <option key={f} value={f}>{f}</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">Availability</label>
                <select
                  value={availabilityFilter}
                  onChange={(e) => setAvailabilityFilter(e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 text-gray-900 bg-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                >
                  <option value="All">All Rooms</option>
                  <option value="Available">Available Only</option>
                  <option value="Full">Full Only</option>
                </select>
              </div>

              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 w-full hover:bg-gray-100 transition">
                  <input
                    type="checkbox"
                    checked={highlightAvailable}
                    onChange={() => setHighlightAvailable(!highlightAvailable)}
                    className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  Highlight available
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-xl shadow-md border border-gray-200 border-t-4 border-t-blue-950 flex flex-col items-center justify-center py-16">
            <div className="w-10 h-10 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin mb-4" />
            <p className="text-sm text-gray-500">Loading room inventory...</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && Object.keys(groupedRooms).length === 0 && (
          <div className="bg-white rounded-xl shadow-md border border-gray-200 border-t-4 border-t-blue-950 flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-indigo-100 to-blue-100 rounded-full flex items-center justify-center mb-4">
              <Home className="w-8 h-8 text-indigo-400" />
            </div>
            <p className="text-sm font-medium text-gray-700">No rooms found</p>
            <p className="text-xs text-gray-500 mt-1">Adjust your filters or search criteria</p>
          </div>
        )}

        {/* Rooms Grid */}
        {!loading && Object.entries(groupedRooms).map(([block, floors]) => (
          <div key={block} className="bg-white rounded-xl shadow-md border border-gray-200 border-t-4 border-t-blue-950 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 via-white to-indigo-50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Building2 className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-gray-900">Block {block}</h2>
                  <p className="text-xs text-gray-500">{Object.values(floors).flat().length} rooms</p>
                </div>
              </div>
            </div>

            <div className="p-5 space-y-6">
              {Object.entries(floors).map(([floor, floorRooms]) => (
                <div key={floor} className="space-y-3">
                  <div className="flex items-center gap-2">
                    <DoorOpen className="w-4 h-4 text-gray-400" />
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Floor {floor}</h3>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {floorRooms.map((room) => {
                      const isAvailable = room.status === "Available";
                      const canAssign = studentId && room.remaining > 0;
                      return (
                        <div
                          key={room.roomId}
                          className={`group rounded-xl p-4 border-2 border-l-4 transition-all duration-200 ${
                            highlightAvailable && isAvailable
                              ? "bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200 border-l-emerald-500 hover:shadow-lg hover:-translate-y-0.5"
                              : "bg-white border-gray-100 border-l-blue-950 hover:border-gray-200 hover:shadow-lg hover:-translate-y-0.5"
                          }`}
                        >
                          <div className="flex justify-between items-start mb-3">
                            <div className="flex items-center gap-2">
                              <div className={`p-1.5 rounded-lg ${isAvailable ? "bg-emerald-100" : "bg-gray-100"}`}>
                                <BedDouble className={`w-4 h-4 ${isAvailable ? "text-emerald-600" : "text-gray-500"}`} />
                              </div>
                              <div>
                                <h4 className="font-semibold text-gray-900 text-sm leading-tight">Room {room.roomNumber}</h4>
                                <p className="text-xs text-gray-500">{room.building} &middot; Floor {room.floor}</p>
                              </div>
                            </div>
                            <span
                              className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full flex-shrink-0 ${
                                isAvailable
                                  ? "bg-emerald-100 text-emerald-700"
                                  : "bg-rose-100 text-rose-700"
                              }`}
                            >
                              {isAvailable ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                              {room.status}
                            </span>
                          </div>

                          <div className="flex gap-2 mb-3">
                            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md bg-blue-50 text-blue-700">
                              <Users className="w-3 h-3" />
                              {room.occupied} occupied
                            </span>
                            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md bg-gray-100 text-gray-600">
                              <BedDouble className="w-3 h-3" />
                              {room.capacity} capacity
                            </span>
                          </div>

                          <div className="flex items-center justify-between text-xs mb-3 bg-gray-50 rounded-lg px-3 py-2">
                            <span className="text-gray-500">Remaining spots</span>
                            <span className={`font-semibold flex items-center gap-1 ${room.remaining > 0 ? "text-emerald-600" : "text-rose-600"}`}>
                              {room.remaining > 0 ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                              {room.remaining}
                            </span>
                          </div>

                          {studentId && (
                            <button
                              onClick={() => {
                                setSelectedRoom(room);
                                setShowAssignConfirm(true);
                              }}
                              disabled={!canAssign || processingRoomId === room.roomId}
                              className={`w-full px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                                !canAssign
                                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                  : "bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 shadow-sm"
                              }`}
                            >
                              {processingRoomId === room.roomId ? (
                                <span className="flex items-center justify-center gap-2">
                                  <FiLoader className="animate-spin" />
                                  Processing
                                </span>
                              ) : (
                                "Assign Student"
                              )}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Assign Confirmation Modal */}
      {showAssignConfirm && selectedRoom && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden border-t-4 border-t-blue-950">
            <div className="px-6 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <BedDouble className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-white">Confirm Assignment</h3>
                <p className="text-xs text-blue-100">This will generate a lease automatically</p>
              </div>
            </div>
            <div className="p-6">
              <div className="bg-gray-50 rounded-xl border border-gray-100 p-4 mb-5">
                <p className="text-xs text-gray-500">Assigning</p>
                <p className="text-sm font-semibold text-gray-900 mt-0.5">
                  {studentFirstName} {studentLastName}
                </p>
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-200">
                  <div className="p-1.5 bg-emerald-100 rounded-lg">
                    <BedDouble className="w-3.5 h-3.5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Room</p>
                    <p className="text-sm font-semibold text-gray-900">
                      Room {selectedRoom.roomNumber} &middot; {selectedRoom.building}, Floor {selectedRoom.floor}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    handleAssign(selectedRoom.roomId);
                    setShowAssignConfirm(false);
                  }}
                  className="flex-1 px-4 py-2.5 text-sm font-medium rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 shadow-sm transition"
                >
                  Confirm Assignment
                </button>
                <button
                  onClick={() => setShowAssignConfirm(false)}
                  className="flex-1 px-4 py-2.5 text-sm font-medium rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RoomsPage;
